import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { listMyAccounts, tenantKeys } from '../api/tenant';
import { useScopeStore } from '../store/scope-store';
import { TEAMS_AVAILABLE } from './teams-available';

/**
 * The switcher's data plus the currently selected scope.
 *
 * `staleTime: 0` + `refetchOnMount: 'always'`, not `Infinity`: team
 * membership changes in flotoapps, not here, so a cached "no teams" (or a
 * cached failure) from before a team was created/left would never refetch on
 * its own — same trap as the credit balance, same fix.
 */
export function useAccounts() {
  const query = useQuery({
    queryFn: listMyAccounts,
    queryKey: tenantKeys.accounts(),
    refetchOnMount: 'always',
    staleTime: 0,
  });
  const selectedTeamId = useScopeStore((state) => state.selectedTeamId);
  const rawSelectTeam = useScopeStore((state) => state.selectTeam);
  const [, setSearchParams] = useSearchParams();

  // Memoized so the effect below (and any consumer's own deps) sees a stable
  // reference across renders that don't change the underlying data — `?? []`
  // alone makes a fresh array every render while data is absent.
  // With teams off nobody has any, so every switcher and team list hides.
  const teams = useMemo(
    () => (TEAMS_AVAILABLE ? (query.data?.teams ?? []) : []),
    [query.data],
  );

  /**
   * Wrap selectTeam to also update URL query params. Merges with existing
   * params to preserve checkout params (session_id, topup, etc).
   */
  const selectTeam = useCallback(
    (teamId: string | null) => {
      rawSelectTeam(teamId);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (teamId) {
          next.set('teamId', teamId);
        } else {
          next.delete('teamId');
        }
        return next;
      });
    },
    [rawSelectTeam, setSearchParams],
  );

  /**
   * Auto-correct a stale selection: a team the caller left, or one that was
   * deleted, since it was last picked. Runs only once the list has actually
   * loaded (`isSuccess`), so a slow first fetch doesn't read as "no teams"
   * and clobber a selection whose team just hasn't arrived yet — and it
   * writes the correction back to the store, not just a local fallback,
   * because `apiFetch` reads the store directly. A read-only fallback here
   * left the real value stale forever: every request kept the old team's
   * header, including this one, which the server now accepts by degrading to
   * personal rather than rejecting (see `TenantService.resolveScope`) — but
   * without this reset, the client would never stop asking for a team it can
   * already see isn't in the list it just got back.
   *
   * Uses rawSelectTeam to avoid creating URL params on auto-correct.
   */
  useEffect(() => {
    if (!query.isSuccess) return;
    if (selectedTeamId && !teams.some((team) => team.id === selectedTeamId)) {
      rawSelectTeam(null);
    }
  }, [query.isSuccess, teams, selectedTeamId, rawSelectTeam]);

  return {
    accountId: query.data?.accountId ?? null,
    loading: query.isLoading,
    selectTeam,
    selectedTeamId,
    teams,
  };
}
